import React, { useMemo, useState } from "react";
import { MatchOfficial, MatchOfficials } from "../matchTypes";
import { PlayerProfile } from "../store/supabasePlayerProfiles";
import { PlayerAvatar } from "./PlayerAvatar";
import {
  OFFICIAL_FIELDS,
  assignedOfficials,
  makeOfficial,
  setOfficial,
} from "../utils/matchOfficials";

// ---------------------------------------------------------------------------
// Read-only display (Live ScoreBox, Match Summary, Completed Scorecard, etc.)
// ---------------------------------------------------------------------------
export const MatchOfficialsDisplay: React.FC<{
  officials?: MatchOfficials | null;
  className?: string;
}> = ({ officials, className }) => {
  const assigned = assignedOfficials(officials);
  if (assigned.length === 0) return null;

  return (
    <div
      className={
        className ??
        "bg-gray-800 rounded-lg border border-gray-700 p-4"
      }
    >
      <h3 className="text-sm font-bold uppercase tracking-wide text-gray-400 mb-3">
        Match Officials
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {assigned.map(({ key, label, official }) => (
          <div key={key} className="flex items-center gap-3 min-w-0">
            <PlayerAvatar name={official.name} photoUrl={official.photoUrl ?? undefined} size="sm" />
            <div className="min-w-0">
              <div className="text-xs text-gray-400 uppercase tracking-wider">{label}</div>
              <div className="text-white font-semibold truncate">{official.name}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Editable card (Match Setup / Match Settings)
// ---------------------------------------------------------------------------
type FieldKey = keyof MatchOfficials;

interface EditorProps {
  officials?: MatchOfficials | null;
  profiles: PlayerProfile[] | null;
  disabled?: boolean;
  // When true (match already started) a confirmation step is required before a change applies.
  requireConfirm?: boolean;
  onChange: (officials: MatchOfficials) => void;
}

export const MatchOfficialsEditor: React.FC<EditorProps> = ({
  officials,
  profiles,
  disabled = false,
  requireConfirm = false,
  onChange,
}) => {
  const [editingKey, setEditingKey] = useState<FieldKey | null>(null);
  const [editingLabel, setEditingLabel] = useState<string>("");
  const [search, setSearch] = useState("");
  const [guestName, setGuestName] = useState("");
  const [pending, setPending] = useState<MatchOfficial | null>(null);
  const [confirming, setConfirming] = useState(false);

  const filteredProfiles = useMemo(() => {
    const list = profiles ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((p) => p.name.toLowerCase().includes(q));
  }, [profiles, search]);

  function openEditor(key: FieldKey, label: string) {
    if (disabled) return;
    setEditingKey(key);
    setEditingLabel(label);
    setSearch("");
    setGuestName("");
    setPending(null);
    setConfirming(false);
  }

  function closeEditor() {
    setEditingKey(null);
    setPending(null);
    setConfirming(false);
  }

  function apply(key: FieldKey, official: MatchOfficial | null) {
    onChange(setOfficial(officials, key, official));
    closeEditor();
  }

  // Route a selection through the confirmation step when the match is live.
  function stage(official: MatchOfficial | null) {
    if (!editingKey) return;
    if (requireConfirm) {
      setPending(official);
      setConfirming(true);
    } else {
      apply(editingKey, official);
    }
  }

  const assignedMap = officials ?? {};

  return (
    <div className="bg-slate-950/50 rounded-lg p-4 border border-slate-800/30 lg:col-span-2">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-lg font-semibold text-slate-300">Match Officials</h3>
        <span className="text-xs text-slate-500">Optional</span>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        Assign officials from your players or add a guest name. All fields are optional.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {OFFICIAL_FIELDS.map(({ key, label }) => {
          const official = assignedMap[key];
          const has = !!official && official.name.trim().length > 0;
          return (
            <div
              key={key}
              className="bg-slate-900/40 border border-slate-700/60 rounded-lg p-3 flex items-center gap-3"
            >
              <div className="flex-1 min-w-0">
                <div className="text-xs text-slate-400 uppercase tracking-wider mb-1">{label}</div>
                {has ? (
                  <div className="flex items-center gap-2 min-w-0">
                    <PlayerAvatar name={official!.name} photoUrl={official!.photoUrl ?? undefined} size="sm" />
                    <span className="text-white font-semibold truncate">{official!.name}</span>
                    {!official!.profileId && (
                      <span className="text-[10px] bg-slate-700 px-1.5 py-0.5 rounded whitespace-nowrap">
                        Guest
                      </span>
                    )}
                  </div>
                ) : (
                  <span className="text-slate-500 text-sm">Not assigned</span>
                )}
              </div>

              {!disabled && (
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => openEditor(key, label)}
                    className="bg-slate-700 hover:bg-slate-600 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
                  >
                    {has ? "Change" : "Assign"}
                  </button>
                  {has && (
                    <button
                      onClick={() => {
                        if (requireConfirm) {
                          openEditor(key, label);
                          setPending(null);
                          setConfirming(true);
                        } else {
                          apply(key, null);
                        }
                      }}
                      className="text-red-400 hover:text-red-300 text-sm font-medium transition-colors"
                    >
                      Clear
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {editingKey && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl max-w-md w-full shadow-2xl max-h-[85vh] flex flex-col">
            <div className="bg-slate-800 border-b border-slate-700 px-6 py-4 rounded-t-xl">
              <h3 className="text-lg font-bold text-white">Assign {editingLabel}</h3>
              {requireConfirm && (
                <p className="text-xs text-amber-400 mt-1">
                  The match has started — you will be asked to confirm this change.
                </p>
              )}
            </div>

            {confirming ? (
              <div className="p-6 space-y-4">
                <p className="text-slate-200">
                  {pending
                    ? `Set ${editingLabel} to "${pending.name}"?`
                    : `Clear ${editingLabel}?`}
                </p>
                <p className="text-xs text-slate-400">
                  Historical matches keep their originally assigned officials. This only updates the
                  current match.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setConfirming(false)}
                    className="flex-1 bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg font-semibold transition-colors"
                  >
                    Back
                  </button>
                  <button
                    onClick={() => editingKey && apply(editingKey, pending)}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors"
                  >
                    Confirm
                  </button>
                </div>
              </div>
            ) : (
              <div className="p-6 space-y-4 overflow-y-auto">
                <div>
                  <label className="block text-sm font-semibold text-slate-400 mb-2">
                    Add a guest name
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={guestName}
                      onChange={(e) => setGuestName(e.target.value)}
                      placeholder="Enter name"
                      className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white"
                    />
                    <button
                      onClick={() => {
                        const official = makeOfficial(guestName);
                        if (official) stage(official);
                      }}
                      disabled={!guestName.trim()}
                      className="bg-green-600 hover:bg-green-700 disabled:opacity-40 text-white px-4 py-2 rounded-lg font-semibold transition-colors"
                    >
                      Add
                    </button>
                  </div>
                </div>

                <div className="border-t border-slate-700 pt-4">
                  <label className="block text-sm font-semibold text-slate-400 mb-2">
                    Or pick an existing player
                  </label>
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search players"
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white mb-3"
                  />
                  <div className="space-y-2 max-h-56 overflow-y-auto">
                    {filteredProfiles.length === 0 ? (
                      <div className="text-slate-500 text-sm text-center py-4">
                        No players found
                      </div>
                    ) : (
                      filteredProfiles.map((p) => (
                        <button
                          key={p.id}
                          onClick={() => stage(makeOfficial(p.name, p.id, p.photo_url))}
                          className="w-full flex items-center gap-3 bg-slate-900/60 hover:bg-slate-700 rounded-lg px-3 py-2 text-left transition-colors"
                        >
                          <PlayerAvatar name={p.name} photoUrl={p.photo_url ?? undefined} size="sm" />
                          <div className="min-w-0">
                            <div className="text-white font-medium truncate">{p.name}</div>
                            {p.role && <div className="text-xs text-slate-400">{p.role}</div>}
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="border-t border-slate-700 px-6 py-3 flex justify-end rounded-b-xl">
              <button
                onClick={closeEditor}
                className="bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg font-semibold transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
